import { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { capitalize, orderBy } from "lodash";
import moment from "moment";
import AutoSizer from "react-virtualized-auto-sizer";

import { useUser } from "@components/user-context";
import { MultiSelect, PieChart, QuickSelectInfoButton } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import {
  CellDateFormatter,
  createColumnHelper,
  HeaderWithInfoIcon,
  Table,
} from "@components/v2/table";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { useNorthbeamQuery } from "@hooks/http/use-northbeam-query";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type FetchTouchpointsByAttributionModeWindowAndModel_me_touchpointsByAttributionModeWindowAndModel } from "@north-beam/nb-client-side";
import {
  type FetchOrder,
  type FetchOrderVariables,
  type FetchTouchpointsByAttributionModeWindowAndModel,
  type FetchTouchpointsByAttributionModeWindowAndModelVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { formatting } from "@north-beam/nb-common";
import {
  FETCH_ORDER,
  FETCH_TOUCHPOINTS_BY_ATTRIBUTION_MODE_WINDOW_AND_MODEL,
} from "@pages/settings/queries";
import { useQueries } from "@tanstack/react-query";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import DefaultCellFormatter from "../table-cells/default-cell-formatter";
import TouchpointsCellFormatter from "../table-cells/touchpoints-cell-formatter";

import { CustomerDetails } from "./customer-details";
import { OrderTable } from "./order-table";

interface OrderDetailsContentProps {
  conversionEventId: string;
}

const NON_SUPPORTED_ATTRIBUTION_MODES = [
  "northbeam_custom__va",
  "northbeam_custom_influencers__va",
  "northbeam_custom__enh",
  "northbeam_custom_matchbacks",
];

const CHART_COLORS = [
  "rgba(144, 224, 239, 1)",
  "rgba(34, 87, 66, 1)",
  "rgba(255, 213, 79, 1)",
  "rgba(229, 80, 57, 1)",
  "rgba(142, 68, 173, 1)",
  "rgba(243, 156, 18, 1)",
];

export const OrderDetailsContent = ({
  conversionEventId,
}: OrderDetailsContentProps) => {
  const { isDemoMode } = usePrefs();
  const { user } = useUser();
  const { addQueryMetadata } = useQueryMetadata();
  const [orderDetails, setOrderDetails] = useState(true);
  const { attributionModel, accountingMode } = useUserAttributionSettings();
  const [localAttributionModels, setLocalAttributionModels] = useState<
    string[]
  >([attributionModel]);

  const columnHelper = createColumnHelper<any>();
  const { loading: orderLoading, data: orderData } = useNorthbeamQuery<
    FetchOrder,
    FetchOrderVariables
  >(FETCH_ORDER, {
    variables: {
      orderNumber: conversionEventId,
      showPriorNonConversions: true,
    },
  });

  const touchpointsQueryResults = useQueries({
    queries: localAttributionModels.map((a) => ({
      queryKey: [conversionEventId, accountingMode, "infinity", a],
      queryFn: async () => {
        const variables = {
          conversionEventId,
          accountingMode,
          // Purposefully hardcoded because we only want to be really comparing customer journeys
          attributionWindowDays: "infinity",
          attributionModel: a,
        };
        return {
          data: await user.apolloClient.query<
            FetchTouchpointsByAttributionModeWindowAndModel,
            FetchTouchpointsByAttributionModeWindowAndModelVariables
          >({
            query: FETCH_TOUCHPOINTS_BY_ATTRIBUTION_MODE_WINDOW_AND_MODEL,
            variables,
          }),
          variables,
        };
      },
    })),
  });

  const isLoading = touchpointsQueryResults.some((result) => result.isLoading);

  const touchpointsAreAttributed = useMemo(() => {
    return touchpointsQueryResults.map((t) => {
      const data =
        t.data?.data.data.me.touchpointsByAttributionModeWindowAndModel
          .isProcessed;
      return data;
    });
  }, [touchpointsQueryResults]);

  const touchPoints = useMemo(() => {
    const rollup: Record<
      string,
      Omit<
        FetchTouchpointsByAttributionModeWindowAndModel_me_touchpointsByAttributionModeWindowAndModel,
        "transactionValue"
      > & { transactionValues: Record<string, number | null> }
    > = {};

    touchpointsQueryResults.forEach((touchpointQueryResult) => {
      const variables = touchpointQueryResult.data?.variables;
      const data =
        touchpointQueryResult.data?.data.data.me
          .touchpointsByAttributionModeWindowAndModel;

      data?.touchpoints.forEach((d) => {
        const { transactionValue, ...rest } = d;
        const key = `${rest.eventTs}-${rest.conversionEventId}-${rest.adKeyHash}`;

        if (!rollup[key]) {
          rollup[key] = {
            ...rest,
            adName: generateAnonBase64Hash({
              value: rest.adName ?? "",
              prefix: "ad",
              doAnon: isDemoMode,
            }),
            adsetName: generateAnonBase64Hash({
              value: rest.adsetName ?? "",
              prefix: "adsetName",
              doAnon: isDemoMode,
            }),
            campaignName: generateAnonBase64Hash({
              value: rest.campaignName ?? "",
              prefix: "campaignName",
              doAnon: isDemoMode,
            }),
            transactionValues: {},
          };
        }

        rollup[key].transactionValues[variables?.attributionModel ?? ""] =
          transactionValue;
      });
    });
    return orderBy(Object.values(rollup), "eventTs", "desc");
  }, [isDemoMode, touchpointsQueryResults]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const paginatedTouchpoints = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return touchPoints.slice(start, end);
  }, [pagination.pageIndex, pagination.pageSize, touchPoints]);

  const { designElementManager } = useDesignElementManager();

  const clientTimeZone = user?.timezone;

  const touchPointsColumns = useMemo(
    () => [
      columnHelper.accessor({
        accessorKey: (r) => r.campaignName || r.adsetName || r.adName,
        columnDef: {
          id: "campaignName",
          sticky: true,
          size: 400,
          cell: (info) => (
            <TouchpointsCellFormatter
              cell={info}
              icon={designElementManager
                .getDesignElement(info.row.original.platformNorthbeamLabelValue)
                .getComponent()}
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Touchpoint" header={header} />;
          },
          enableSorting: false,
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.eventTs,
        columnDef: {
          enableSorting: false,
          id: "eventTs",
          size: 240,
          cell: (info) => (
            <CellDateFormatter
              cell={info}
              value={moment(info.getValue())
                .tz(clientTimeZone)
                .format("YYYY-MM-DD HH:mm:ss z")}
              align="left"
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Date" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.kind,
        columnDef: {
          enableSorting: false,
          id: "kind",
          cell: (info) => <DefaultCellFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Kind" header={header} />;
          },
        },
      }),

      ...(localAttributionModels.length
        ? localAttributionModels.map((a) => {
            const id = `${a}`;
            return columnHelper.accessor({
              accessorKey: (r) => r.transactionValues[a],
              columnDef: {
                enableSorting: false,
                id: id,

                cell: (info) => (
                  <DefaultCellFormatter
                    value={formatting.formatMetric.exact(
                      info.getValue(),
                      "dollars",
                      {
                        code: user.currencies.currentCurrency,
                      },
                    )}
                    cell={info}
                    align="right"
                  />
                ),
                header: (header) => {
                  return (
                    <HeaderWithInfoIcon
                      title={
                        user.attributionMethodEnum.reactSelectOptions.find(
                          (option) => option.value === a,
                        )?.label || ""
                      }
                      header={header}
                      description="Orders are processed daily."
                    />
                  );
                },
              },
            });
          })
        : []),
    ],
    [
      clientTimeZone,
      columnHelper,
      designElementManager,
      localAttributionModels,
      user.attributionMethodEnum.reactSelectOptions,
      user.currencies.currentCurrency,
    ],
  );

  const touchPointsData = useMemo(() => {
    if (!touchPoints.length) {
      return [];
    }

    const groupedTouchpoints = touchPoints.reduce((acc, tp) => {
      const key =
        tp.platformNorthbeamLabelValue ||
        capitalize(tp.platform ?? "") ||
        capitalize(tp.kind ?? "");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(groupedTouchpoints).map(([label, value], index) => ({
      label,
      value,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));
  }, [touchPoints]);

  const transformedBqLogs = useMemo(() => {
    return touchpointsQueryResults
      .map(
        (result) =>
          result.data?.data.data.me.touchpointsByAttributionModeWindowAndModel
            ?.queryMetadata?.bqLogs ?? [],
      )
      .flat()
      .map((bqLog) => ({
        __typename: "BigQueryLog" as const,
        query: bqLog.query,
        queryParameters: bqLog.queryParameters,
        queryTypes: bqLog.queryTypes,
        labels: null,
        jobId: null,
        queryId: null,
        executionTimestamp: null,
      }));
  }, [touchpointsQueryResults]);

  const prevLogsRef = useRef(transformedBqLogs);

  useEffect(() => {
    const areLogsEqual =
      JSON.stringify(transformedBqLogs) === JSON.stringify(prevLogsRef.current);
    if (!areLogsEqual) {
      addQueryMetadata({
        context: "Orders Details",
        topic: "Touchpoints",
        metadata: {
          __typename: "QueryMetadata",
          bqLogs: transformedBqLogs,
        },
      });

      prevLogsRef.current = transformedBqLogs;
    }
  }, [transformedBqLogs]);

  return (
    <div className="h-full flex gap-6 overflow-hidden">
      <div
        className={clsx(
          "overflow-hidden p-4 rounded-lg border border-v2-secondary bg-v2-highlight border-solid",
          "w-[450px] flex gap-2 flex-col h-full justify-start",
        )}
      >
        <div className="w-full flex gap-2">
          <QuickSelectInfoButton
            className="flex-1"
            onClick={() => {
              setOrderDetails(true);
            }}
            selected={orderDetails}
          >
            Order Details
          </QuickSelectInfoButton>
          <QuickSelectInfoButton
            className="flex-1"
            onClick={() => {
              setOrderDetails(false);
            }}
            selected={!orderDetails}
          >
            Customer Details
          </QuickSelectInfoButton>
        </div>
        {orderDetails && !orderLoading && (
          <>
            <div className="flex-center">
              <div className="flex items-center py-6 justify-center">
                {touchPointsData.length > 0 && (
                  <PieChart
                    data={touchPointsData}
                    size={250}
                    showLegend={true}
                    showTooltip={true}
                  />
                )}
              </div>
            </div>
            <div className="text-xs font-bold uppercase">Order</div>
            <div className="flex flex-col gap-2 w-full overflow-hidden">
              <OrderTable orderData={orderData} />
            </div>
          </>
        )}
        {!orderDetails && (
          <div className="flex flex-col gap-2 w-full overflow-hidden">
            <CustomerDetails
              customerDetails={{
                customerEmail: orderData?.me?.order?.customerEmail || "",
                customerFirstName:
                  orderData?.me?.order?.customerFirstName || "",
                customerId: orderData?.me?.order?.customerId || "",
                customerPhone: orderData?.me?.order?.customerPhone || "",
                orderType: orderData?.me?.order?.orderType || "",
                shippingAddress1: orderData?.me?.order?.shippingAddress1 || "",
                shippingAddress2: orderData?.me?.order?.shippingAddress2 || "",
                shippingCity: orderData?.me?.order?.shippingCity || "",
                shippingCountry: orderData?.me?.order?.shippingCountry || "",
                shippingCountryCode:
                  orderData?.me?.order?.shippingCountryCode || "",
                shippingProvince: orderData?.me?.order?.shippingProvince || "",
                shippingProvinceCode:
                  orderData?.me?.order?.shippingProvinceCode || "",
                shippingZip: orderData?.me?.order?.shippingZip || "",
              }}
            />
          </div>
        )}
      </div>
      <div className="flex justify-end absolute top-10 right-6 items-center gap-4">
        {!isLoading &&
          touchpointsAreAttributed.every((isAttributed) => !isAttributed) && (
            <div className="text-xs text-v2-secondary italic">
              *Attribution is being calculated for each touchpoint. Please check
              back later.
            </div>
          )}
        <MultiSelect
          size="md"
          buttonClassName="w-[200px]"
          options={user.attributionMethodEnum.reactSelectOptions.filter(
            (option) => !NON_SUPPORTED_ATTRIBUTION_MODES.includes(option.value),
          )}
          values={localAttributionModels}
          onChange={(value) => {
            setLocalAttributionModels(value || []);
          }}
        />
      </div>
      <div className="flex gap-2 flex-column flex-1">
        <AutoSizer>
          {({ height, width }) => {
            return (
              <div style={{ height: height - 50, width }}>
                <Table
                  className="h-full"
                  enableResizing={true}
                  columns={touchPointsColumns}
                  loading={touchpointsQueryResults.some((t) => t.isLoading)}
                  rows={paginatedTouchpoints}
                  multiSort={false}
                  rowCount={touchPoints.length}
                  onPaginationChange={(newPagination) => {
                    setPagination(newPagination);
                  }}
                  pagination={pagination}
                />
              </div>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};
